import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, RotateCcw, Download, Plus, X, ChevronDown, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const input = "w-full bg-[#0f0f11] border border-white/15 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 px-3 py-2 text-sm outline-none transition-colors";
const label = "block text-[0.65rem] uppercase tracking-[0.18em] text-stone-500 mb-2";
const MATCHES = ["broad", "phrase", "exact"];

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const downloadCsv = (name, rows) => {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminAds() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({});
  const [newKw, setNewKw] = useState({});
  const [newNeg, setNewNeg] = useState({});

  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ads-plan");
      setPlan(data);
    } catch { toast.error("Could not load ads plan."); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const update = (fn) => { setPlan((p) => { const n = structuredClone(p); fn(n); return n; }); setDirty(true); };
  const setCampaign = (ci, patch) => update((n) => Object.assign(n.campaigns[ci], patch));
  const setGroup = (ci, gi, patch) => update((n) => Object.assign(n.campaigns[ci].ad_groups[gi], patch));

  const addKeyword = (ci, gi) => {
    const key = `${ci}-${gi}`;
    const text = (newKw[key] || "").trim();
    if (!text) return;
    update((n) => n.campaigns[ci].ad_groups[gi].keywords.push({ text, match: "phrase" }));
    setNewKw((s) => ({ ...s, [key]: "" }));
  };
  const addNegative = (scope, ci, gi) => {
    const key = scope === "account" ? "account" : `${ci}-${gi}`;
    const text = (newNeg[key] || "").trim();
    if (!text) return;
    update((n) => {
      const arr = scope === "account" ? n.account_negatives : n.campaigns[ci].ad_groups[gi].negatives;
      arr.push({ text, match: "broad" });
    });
    setNewNeg((s) => ({ ...s, [key]: "" }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ads-plan", plan);
      setPlan(data); setDirty(false);
      toast.success("Ads plan saved.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the whole plan back to the recommended setup? Your edits will be lost.")) return;
    try {
      const { data } = await api.post("/admin/ads-plan/reset");
      setPlan(data); setDirty(false);
      toast.success("Plan reset to recommended.");
    } catch { toast.error("Reset failed."); }
  };

  const exportKeywords = () => {
    const rows = [["Campaign", "Ad Group", "Keyword", "Match Type", "Final URL"]];
    plan.campaigns.forEach((c) => c.ad_groups.forEach((g) => g.keywords.forEach((k) =>
      rows.push([c.name, g.name, k.text, k.match[0].toUpperCase() + k.match.slice(1), g.landing_page]))));
    downloadCsv("ptwa-google-ads-keywords.csv", rows);
  };
  const exportNegatives = () => {
    const rows = [["Level", "Campaign", "Ad Group", "Keyword", "Match Type"]];
    plan.account_negatives.forEach((k) => rows.push(["Account", "", "", k.text, k.match[0].toUpperCase() + k.match.slice(1)]));
    plan.campaigns.forEach((c) => c.ad_groups.forEach((g) => g.negatives.forEach((k) =>
      rows.push(["Ad Group", c.name, g.name, k.text, k.match[0].toUpperCase() + k.match.slice(1)]))));
    downloadCsv("ptwa-google-ads-negative-keywords.csv", rows);
  };
  const exportAds = () => {
    const rows = [["Campaign", "Ad Group", "Headline 1", "Headline 2", "Headline 3", "Headline 4", "Headline 5", "Headline 6", "Description 1", "Description 2", "Final URL"]];
    plan.campaigns.forEach((c) => c.ad_groups.forEach((g) => (g.ads || []).forEach((a) =>
      rows.push([c.name, g.name, ...(a.headlines || []).slice(0, 6), ...Array(Math.max(0, 6 - (a.headlines || []).length)).fill(""), ...(a.descriptions || []).slice(0, 2), ...Array(Math.max(0, 2 - (a.descriptions || []).length)).fill(""), g.landing_page]))));
    downloadCsv("ptwa-google-ads-ads.csv", rows);
  };
  const exportCampaigns = () => {
    const rows = [["Campaign", "Campaign Type", "Daily Budget (AUD)", "Est. Monthly (AUD)"]];
    plan.campaigns.forEach((c) => rows.push([c.name, "Search", c.daily_budget, Math.round(c.daily_budget * 30.4)]));
    downloadCsv("ptwa-google-ads-campaigns.csv", rows);
  };

  if (!ready || !user || !plan) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /></div>;
  }

  const totalDaily = plan.campaigns.reduce((s, c) => s + (Number(c.daily_budget) || 0), 0);
  const kwCount = plan.campaigns.reduce((s, c) => s + c.ad_groups.reduce((x, g) => x + g.keywords.length, 0), 0);
  const negCount = plan.account_negatives.length + plan.campaigns.reduce((s, c) => s + c.ad_groups.reduce((x, g) => x + g.negatives.length, 0), 0);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-stone-200" data-testid="admin-ads-page">
      <div className="grain-overlay" aria-hidden />

      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/logo.png" alt="Perth Tiling WA" className="h-9 md:h-10 w-auto object-contain" />
            <span className="hidden sm:inline text-stone-500 text-xs uppercase tracking-[0.2em] truncate">Google Ads Planner</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/admin")} data-testid="ads-back-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-400 hover:text-white border border-white/15 hover:border-white/30 px-3 py-2 transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <button onClick={reset} data-testid="ads-reset-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-400 hover:text-white border border-white/15 hover:border-white/30 px-3 py-2 transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Reset</span>
            </button>
            <button onClick={save} disabled={saving || !dirty} data-testid="ads-save-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] bg-[#d4c4b7] text-black hover:bg-white disabled:opacity-40 px-4 py-2 transition-colors">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save{dirty ? "*" : ""}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="overline text-[#d4c4b7] mb-3 flex items-center gap-2"><Megaphone className="h-3.5 w-3.5" /> Ready for Google Ads</p>
            <h1 className="font-serif text-4xl md:text-5xl text-stone-50 font-light">Campaign Plan</h1>
            <p className="text-stone-500 font-light mt-3 max-w-xl text-sm leading-relaxed">
              Pre-loaded with Perth tiling keywords, negative keywords, budgets and ad copy. Edit anything, hit Save, then export the CSVs into Google Ads (or Google Ads Editor) when your account is ready. Full walkthrough in GOOGLE_ADS_SETUP.md.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="ads-export-buttons">
            <button onClick={exportKeywords} data-testid="export-keywords-btn" className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.15em] text-[#d4c4b7] hover:text-white border border-[#d4c4b7]/40 hover:border-[#d4c4b7] px-3 py-2 transition-colors"><Download className="h-3 w-3" /> Keywords CSV</button>
            <button onClick={exportNegatives} data-testid="export-negatives-btn" className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.15em] text-[#d4c4b7] hover:text-white border border-[#d4c4b7]/40 hover:border-[#d4c4b7] px-3 py-2 transition-colors"><Download className="h-3 w-3" /> Negatives CSV</button>
            <button onClick={exportAds} data-testid="export-ads-btn" className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.15em] text-[#d4c4b7] hover:text-white border border-[#d4c4b7]/40 hover:border-[#d4c4b7] px-3 py-2 transition-colors"><Download className="h-3 w-3" /> Ads CSV</button>
            <button onClick={exportCampaigns} data-testid="export-campaigns-btn" className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.15em] text-[#d4c4b7] hover:text-white border border-[#d4c4b7]/40 hover:border-[#d4c4b7] px-3 py-2 transition-colors"><Download className="h-3 w-3" /> Budgets CSV</button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-12" data-testid="ads-summary">
          {[
            ["Daily Budget", `$${totalDaily}`],
            ["Est. Monthly", `$${Math.round(totalDaily * 30.4).toLocaleString()}`],
            ["Campaigns", plan.campaigns.length],
            ["Keywords / Negatives", `${kwCount} / ${negCount}`],
          ].map(([l, v]) => (
            <div key={l} className="bg-[#0f0f11] p-6">
              <p className="overline text-stone-500 mb-2">{l}</p>
              <p className="font-serif text-3xl text-stone-50">{v}</p>
            </div>
          ))}
        </div>

        <div className="mb-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-[#0f0f11] p-6">
            <p className={label}>Location targeting</p>
            <input value={plan.location_targeting} onChange={(e) => update((n) => { n.location_targeting = e.target.value; })} data-testid="ads-location-input" className={input} />
          </div>
          <div className="border border-white/10 bg-[#0f0f11] p-6">
            <p className={label}>Schedule note</p>
            <input value={plan.schedule_note} onChange={(e) => update((n) => { n.schedule_note = e.target.value; })} data-testid="ads-schedule-input" className={input} />
          </div>
        </div>

        {/* Campaigns */}
        <div className="space-y-6">
          {plan.campaigns.map((c, ci) => (
            <section key={c.id || ci} className="border border-white/10 bg-[#0f0f11]" data-testid={`ads-campaign-${c.id}`}>
              <button onClick={() => setOpen((s) => ({ ...s, [ci]: !s[ci] }))} data-testid={`ads-campaign-toggle-${c.id}`} className="w-full flex items-center justify-between gap-4 p-6 text-left">
                <div className="flex items-center gap-4 min-w-0">
                  <ChevronDown className={`h-4 w-4 text-[#d4c4b7] shrink-0 transition-transform ${open[ci] ? "" : "-rotate-90"}`} />
                  <div className="min-w-0">
                    <h2 className="font-serif text-2xl text-stone-50 truncate">{c.name}</h2>
                    <p className="text-stone-500 text-xs mt-1">{c.ad_groups.length} ad groups · {c.ad_groups.reduce((s, g) => s + g.keywords.length, 0)} keywords</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-serif text-2xl text-[#d4c4b7]">${c.daily_budget}/day</p>
                  <p className="text-stone-500 text-xs">≈ ${Math.round((Number(c.daily_budget) || 0) * 30.4)}/mo</p>
                </div>
              </button>

              {open[ci] && (
                <div className="border-t border-white/10 p-6 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={label}>Campaign name</p>
                      <input value={c.name} onChange={(e) => setCampaign(ci, { name: e.target.value })} data-testid={`ads-campaign-name-${c.id}`} className={input} />
                    </div>
                    <div>
                      <p className={label}>Daily budget (AUD)</p>
                      <input type="number" min="0" value={c.daily_budget} onChange={(e) => setCampaign(ci, { daily_budget: Number(e.target.value) || 0 })} data-testid={`ads-campaign-budget-${c.id}`} className={input} />
                    </div>
                    <div>
                      <p className={label}>Strategy note</p>
                      <input value={c.notes || ""} onChange={(e) => setCampaign(ci, { notes: e.target.value })} className={input} />
                    </div>
                  </div>

                  {c.ad_groups.map((g, gi) => (
                    <div key={g.id || gi} className="border border-white/10 bg-[#0a0a0c] p-5" data-testid={`ads-group-${g.id}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                        <div>
                          <p className={label}>Ad group</p>
                          <input value={g.name} onChange={(e) => setGroup(ci, gi, { name: e.target.value })} className={input} />
                        </div>
                        <div>
                          <p className={label}>Landing page</p>
                          <input value={g.landing_page} onChange={(e) => setGroup(ci, gi, { landing_page: e.target.value })} data-testid={`ads-group-landing-${g.id}`} className={input} />
                        </div>
                      </div>

                      {/* Keywords */}
                      <p className={label}>Keywords ({g.keywords.length})</p>
                      <div className="space-y-2 mb-3">
                        {g.keywords.map((k, ki) => (
                          <div key={ki} className="flex items-center gap-2" data-testid={`ads-keyword-${g.id}-${ki}`}>
                            <input value={k.text} onChange={(e) => update((n) => { n.campaigns[ci].ad_groups[gi].keywords[ki].text = e.target.value; })} className={input} />
                            <select value={k.match} onChange={(e) => update((n) => { n.campaigns[ci].ad_groups[gi].keywords[ki].match = e.target.value; })} className={`${input} !w-28 shrink-0 cursor-pointer`}>
                              {MATCHES.map((m) => <option key={m} value={m} className="bg-[#141416]">{m}</option>)}
                            </select>
                            <button onClick={() => update((n) => { n.campaigns[ci].ad_groups[gi].keywords.splice(ki, 1); })} data-testid={`ads-keyword-remove-${g.id}-${ki}`} className="shrink-0 text-stone-500 hover:text-red-400 p-2 transition-colors" aria-label="Remove keyword"><X className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mb-6">
                        <input value={newKw[`${ci}-${gi}`] || ""} onChange={(e) => setNewKw((s) => ({ ...s, [`${ci}-${gi}`]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addKeyword(ci, gi)} placeholder="Add keyword…" data-testid={`ads-keyword-add-${g.id}`} className={input} />
                        <button onClick={() => addKeyword(ci, gi)} className="shrink-0 inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.15em] text-[#d4c4b7] border border-[#d4c4b7]/40 hover:border-[#d4c4b7] px-3 py-2 transition-colors"><Plus className="h-3 w-3" /> Add</button>
                      </div>

                      {/* Group negatives */}
                      {g.negatives.length > 0 && (
                        <>
                          <p className={label}>Group negative keywords</p>
                          <div className="flex flex-wrap gap-2 mb-6">
                            {g.negatives.map((n, ni) => (
                              <span key={ni} className="inline-flex items-center gap-2 text-xs text-stone-400 border border-white/15 px-2.5 py-1">
                                {n.text}
                                <button onClick={() => update((p) => { p.campaigns[ci].ad_groups[gi].negatives.splice(ni, 1); })} className="text-stone-500 hover:text-red-400" aria-label="Remove negative"><X className="h-3 w-3" /></button>
                              </span>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Ads */}
                      {(g.ads || []).map((a, ai) => (
                        <div key={ai}>
                          <p className={label}>Responsive search ad</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              {a.headlines.map((h, hi) => (
                                <div key={hi} className="flex items-center gap-2">
                                  <input value={h} maxLength={30} onChange={(e) => update((n) => { n.campaigns[ci].ad_groups[gi].ads[ai].headlines[hi] = e.target.value; })} className={input} />
                                  <span className={`text-[0.6rem] shrink-0 w-8 text-right ${h.length > 30 ? "text-red-400" : "text-stone-600"}`}>{h.length}/30</span>
                                </div>
                              ))}
                              <p className="text-[0.6rem] text-stone-600">Headlines — max 30 characters</p>
                            </div>
                            <div className="space-y-2">
                              {a.descriptions.map((d, di) => (
                                <div key={di} className="flex items-center gap-2">
                                  <input value={d} maxLength={90} onChange={(e) => update((n) => { n.campaigns[ci].ad_groups[gi].ads[ai].descriptions[di] = e.target.value; })} className={input} />
                                  <span className={`text-[0.6rem] shrink-0 w-8 text-right ${d.length > 90 ? "text-red-400" : "text-stone-600"}`}>{d.length}/90</span>
                                </div>
                              ))}
                              <p className="text-[0.6rem] text-stone-600">Descriptions — max 90 characters</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Account-level negatives */}
        <section className="border border-white/10 bg-[#0f0f11] p-6 mt-10" data-testid="ads-account-negatives">
          <h2 className="font-serif text-2xl text-stone-50 mb-2">Account-Level Negative Keywords</h2>
          <p className="text-stone-500 text-sm font-light mb-5">Applied to every campaign — blocks job seekers, DIY researchers, tile shoppers and interstate searches.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {plan.account_negatives.map((n, ni) => (
              <span key={ni} data-testid={`ads-account-negative-${ni}`} className="inline-flex items-center gap-2 text-xs text-stone-300 border border-white/15 px-2.5 py-1">
                {n.text} <span className="text-stone-600">({n.match})</span>
                <button onClick={() => update((p) => { p.account_negatives.splice(ni, 1); })} className="text-stone-500 hover:text-red-400" aria-label="Remove negative"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2 max-w-md">
            <input value={newNeg.account || ""} onChange={(e) => setNewNeg((s) => ({ ...s, account: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addNegative("account")} placeholder="Add account negative…" data-testid="ads-account-negative-add" className={input} />
            <button onClick={() => addNegative("account")} className="shrink-0 inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.15em] text-[#d4c4b7] border border-[#d4c4b7]/40 hover:border-[#d4c4b7] px-3 py-2 transition-colors"><Plus className="h-3 w-3" /> Add</button>
          </div>
        </section>
      </main>
    </div>
  );
}
